import { useQuery } from "@tanstack/react-query";
import { ArrowDownLeft, ArrowUpRight, MessageCircle, Wallet as WalletIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { walletApi } from "@/features/wallet/api";
import { useAuth } from "@/features/auth/AuthContext";

/**
 * TSGo Wallet — customer view.
 * V1.0: no self-recharge. Customers request top-ups via WhatsApp; Super Admin
 * credits their wallet from the admin panel.
 */
export default function WalletPage() {
    const { user } = useAuth();
    const { data: summary, isLoading } = useQuery({
        queryKey: ["wallet", "summary"],
        queryFn: walletApi.summary,
        refetchInterval: 15000,
    });
    const { data: txns } = useQuery({
        queryKey: ["wallet", "transactions"],
        queryFn: () => walletApi.transactions(1, 50),
    });

    const buildWhatsAppLink = () => {
        // Official TSGo WhatsApp Business number — single source of truth.
        // We intentionally ignore any override in payment_settings so support
        // links are consistent across every customer install.
        const TSGO_WHATSAPP = "917330827980";
        const text = [
            "Hello TSGo,",
            "I want to recharge my TSGo Wallet.",
            "",
            "Registered Mobile Number:",
            user?.phone || "________",
            "",
            "Amount:",
            "₹_____",
            "",
            "Please add money to my wallet.",
        ].join("\n");
        return `https://wa.me/${TSGO_WHATSAPP}?text=${encodeURIComponent(text)}`;
    };

    return (
        <div data-testid="wallet-page" className="mx-auto max-w-3xl space-y-6 px-4 py-6">
            <header className="flex items-center gap-3">
                <span className="grid h-11 w-11 place-items-center rounded-2xl bg-brand text-white">
                    <WalletIcon className="h-5 w-5" />
                </span>
                <div>
                    <h1 className="text-2xl font-extrabold">TSGo Wallet</h1>
                    <p className="text-sm text-muted-foreground">
                        Pay from your balance at checkout — no gateway fees.
                    </p>
                </div>
            </header>

            {/* Balance card */}
            <section className="rounded-2xl bg-ink p-6 text-white shadow-lg" data-testid="wallet-balance-card">
                <p className="text-sm uppercase tracking-widest text-white/60">Current balance</p>
                <p className="mt-2 text-4xl font-extrabold" data-testid="wallet-balance">
                    ₹{isLoading ? "…" : (summary?.balance ?? 0).toLocaleString("en-IN")}
                </p>
                {summary?.status === "frozen" && (
                    <p className="mt-2 text-xs font-semibold text-amber-300">
                        Wallet frozen — contact support.
                    </p>
                )}
            </section>

            {/* WhatsApp recharge CTA */}
            <section className="rounded-2xl border border-dashed border-brand/40 bg-brand/5 p-5">
                <p className="text-sm font-bold">Need to add money to your TSGo Wallet?</p>
                <p className="mt-1 text-xs text-muted-foreground">
                    Message our team on WhatsApp — we'll credit your wallet after verifying the transfer.
                </p>
                <Button
                    asChild
                    className="mt-4 bg-[#25D366] text-white hover:bg-[#1ebe57]"
                    data-testid="wallet-whatsapp-cta"
                >
                    <a href={buildWhatsAppLink()} target="_blank" rel="noreferrer">
                        <MessageCircle className="mr-2 h-4 w-4" />
                        Chat with TSGo on WhatsApp
                    </a>
                </Button>
            </section>

            {/* Transaction list */}
            <section>
                <div className="mb-3 flex items-center justify-between">
                    <h2 className="text-lg font-bold">Transaction history</h2>
                    <p className="text-xs text-muted-foreground">
                        {txns?.total ?? 0} entr{(txns?.total ?? 0) === 1 ? "y" : "ies"}
                    </p>
                </div>
                {!txns?.items?.length ? (
                    <div className="rounded-2xl border border-dashed border-border/60 bg-white p-10 text-center text-sm text-muted-foreground">
                        No wallet activity yet.
                    </div>
                ) : (
                    <ul className="space-y-2">
                        {txns.items.map((t) => (
                            <li
                                key={t.id}
                                data-testid={`wallet-txn-${t.reference}`}
                                className="flex items-center justify-between rounded-xl border border-border/60 bg-white p-4"
                            >
                                <div className="flex items-center gap-3">
                                    <span
                                        className={`grid h-9 w-9 place-items-center rounded-full ${
                                            t.kind === "credit"
                                                ? "bg-emerald-100 text-emerald-600"
                                                : "bg-rose-100 text-rose-600"
                                        }`}
                                    >
                                        {t.kind === "credit" ? (
                                            <ArrowDownLeft className="h-4 w-4" />
                                        ) : (
                                            <ArrowUpRight className="h-4 w-4" />
                                        )}
                                    </span>
                                    <div>
                                        <p className="text-sm font-semibold">{t.reason}</p>
                                        <p className="text-xs text-muted-foreground">
                                            {new Date(t.at).toLocaleString()} · {t.reference}
                                        </p>
                                    </div>
                                </div>
                                <div className="text-right">
                                    <p
                                        className={`text-sm font-bold ${
                                            t.kind === "credit" ? "text-emerald-600" : "text-rose-600"
                                        }`}
                                    >
                                        {t.kind === "credit" ? "+" : "−"}₹{t.amount.toLocaleString("en-IN")}
                                    </p>
                                    <p className="text-xs text-muted-foreground">
                                        Bal ₹{t.balance_after.toLocaleString("en-IN")}
                                    </p>
                                </div>
                            </li>
                        ))}
                    </ul>
                )}
            </section>
        </div>
    );
}
